import { useEffect, useState} from 'react';
import { Card } from '../../components/Card';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../redux/store';
import {  changeFileSpeed, changePrecheckApiError, changeSelectedMic } from '../../redux/appConfigSlice';
import { Text } from '../../components/Text';
import SpeakCheckScreen, { ISpeakCheckRules } from './SpeakCheckScreen';
import classes from './MicrophoneSelection.module.css';
import { MicrophoneErrorCard } from '../../components/MicrophoneErrorCard/MicrophoneErrorCard';
import { getDownloadFile, fetchTestRules, savePrecheckLogs } from '../../api/authApis';
import { ErrorCard } from '../../components/ErrorCard';
import { getSpeakingTestFormattedDate } from '../../service/utils';
import { pushFaroEvent } from '../../faroSession';

interface MicrophoneSelectionMainProps { 
    workerRef: React.MutableRefObject<Worker | null>;
    ready: boolean;
    rules: ISpeakCheckRules;
    errorCode?: string;
}

interface MicrophoneErrorScreenProps {
    isMicAccessEnabled: boolean;
    loading: boolean;
}

/* Main component for the microphone selection screen */
export default function MicrophoneSelectionMain(props: Readonly<MicrophoneSelectionMainProps>) {
    const {
        appMicAccess,
        selectedMic,
        silenceMeanValue,
        testTaker: testTakerDetails,
        appFontSize: fontSize,
        isSilent,
        fileSpeed,
        soundCheckScreenAudio,
        appTheme,
        speakCheckScreenDetails,
        precheckApiError,
        pageObject: { isMicSelected },
        insperaDetails,
    } = useSelector((state: RootState) => state.appConfigSlice);
    const { precheckStartTimestamp } = useSelector(
        (state: RootState) => state.appConfigSlice
    );
    const dispatch = useDispatch<AppDispatch>();
    const [micAccess, setMicAccess] = useState(appMicAccess);
    const { workerRef, ready, rules, errorCode } = props;

    const [isSpeakCheckFinished, setIsSpeakCheckFinished] = useState<boolean>(false);
    const [downloadSpeed, setDownloadSpeed] = useState('');
    function causeOfError() {
        let error = null;
        if (selectedMic?.label.trim() === '' && appMicAccess) {
            error = 'Microphone Notfound';
        } else if (isSilent) {
            error = 'Threshold not passed';
        } else if (precheckApiError) {
            error = precheckApiError;
        }
        return error;
    }
    let errorMsg = causeOfError();
    let cmsId = testTakerDetails?.testTakerSessionId;
    let cmsSessionId = cmsId.replace(/[:.;]/g, '_');
    let fileName = `preCheckLog_${cmsSessionId}.txt`;
    const payload = {
        testTakerId: testTakerDetails?.testTakerId,
        cmsSessionId: testTakerDetails?.testTakerSessionId,
        centerName: testTakerDetails?.testTakerCenterName,
        language: testTakerDetails?.testTakerLanguage,
        status: 'Prechecks fail',
        productType: insperaDetails?.product === 'CRELLA' ? 'GT' : insperaDetails?.product,
        fileName: fileName,
        partner: insperaDetails?.partner,
        product: insperaDetails?.product,
        assessmentId: insperaDetails?.assessmentId,
        externalId: insperaDetails?.externalId,
        uttId: insperaDetails?.globalReferenceId,
        testDate: getSpeakingTestFormattedDate().utc,
        testCentreTimeZone: getSpeakingTestFormattedDate().localTimezone,
        recordingStartTimestamp: precheckStartTimestamp,
        recordingEndTimestamp: null,
        preCheckLog: {
            fontSize: fontSize,
            contrast: appTheme,
            speckerCheckScreenAudio: soundCheckScreenAudio ? 'played' : 'not-played',
            micCheck: selectedMic?.label,
            uploadSpeed: fileSpeed?.uploadSpeed,
            downloadSpeed: fileSpeed?.downloadSpeed || downloadSpeed,
            thresholdValue: silenceMeanValue,
            filePathOfRecording: speakCheckScreenDetails?.responseFilePath,
            duration: speakCheckScreenDetails?.duration,
            error: errorMsg,
        },
    };

    useEffect(() => {
        if (errorMsg) {
            pushFaroEvent('precheck_failed', { reason: errorMsg });
           savePrecheckLogs(testTakerDetails, {
            ...payload,
            recordingEndTimestamp: new Date().toISOString()
        });
        }
    }, [errorMsg]);

    useEffect(() => {
        if (isSpeakCheckFinished) {
            pushFaroEvent('precheck_completed');
        }
    }, [isSpeakCheckFinished]);

    useEffect(() => {
        let isCancelled = false;
        const startDownloadSpeed = async () => {
            try {
                const result = await fetchDownloadFile();
                if (!isCancelled && result) {
                    setDownloadSpeed(`${result.avgSpeed.toFixed(2)} Mbps`);
                }
            } catch (error) {
                if (!isCancelled) {
                    setDownloadSpeed('');
                }
            }
        };
        // startDownloadSpeed(); -- Commented out as we are not using this function for now, but keeping it for future use if needed.
        return () => {
            isCancelled = true;
        };
    }, []);


    const fetchDownloadFile = async () => {
        const iterations = 1;
        let totalSpeed = 0;
        let totalSizeInKB = 0;
        const speeds: number[] = [];

        for (let i = 0; i < iterations; i++) {
            try {
                const startTime = performance.now();
                const res = await getDownloadFile(
                    testTakerDetails.testTakerSessionId,
                    testTakerDetails.testTakerId,
                    testTakerDetails.testTakerAccessToken,
                );
                const endTime = performance.now();

                // get raw size directly
                const fileSizeInBytes = res.data.byteLength || res.data.length;
                const durationInSeconds = (endTime - startTime) / 1000;

                const speedMbps = fileSizeInBytes / durationInSeconds / 125000;
                speeds.push(speedMbps);
                totalSpeed += speedMbps;
                totalSizeInKB += fileSizeInBytes / 1024;
            } catch (error) {
                console.error(`Download failed on iteration ${i + 1}`, error);
                dispatch(changePrecheckApiError('Error during Download Api'));
            }
        }
        const avgSpeed = speeds.length > 0 ?  totalSpeed / speeds.length: 0;
        pushFaroEvent('download_speed_check_completed', { 
            avgSpeed: avgSpeed.toFixed(2),
            avgFileSizeKB: (totalSizeInKB / iterations).toFixed(2)
        });
        dispatch(changeFileSpeed({
            downloadSpeed: avgSpeed.toFixed(2),
        }));
        return { avgSpeed };
    };


    const handleDeviceChange = async () => {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const micList = devices.filter((d) => d.kind === 'audioinput');
            let filteredMicList: any = [];
            pushFaroEvent('microphone_list_updated_on_change', { count: String(micList.length) });
            micList.forEach((mic) => {
                if (/^[0-9a-f]+$/i.test(mic.deviceId) ) {
                    filteredMicList.push({
                        label: mic.label,
                        deviceId: mic.deviceId,
                        groupId: mic.groupId,
                    });
                }
            });
            if (filteredMicList.length != 0) {
                //Set mic access
                setMicAccess(appMicAccess);

                dispatch(
                    changeSelectedMic({
                        deviceId: filteredMicList[0].deviceId,
                        label: filteredMicList[0].label,
                    }),
                );
            } else {
                dispatch(
                    changeSelectedMic({
                        deviceId: '',
                        label: '',
                    }),
                );
            }
        } catch (error) {
            console.error('Error detecting device changes:', error);
        }
    };

    useEffect(() => {
        handleDeviceChange();
        navigator.mediaDevices.ondevicechange = handleDeviceChange;
        return () => {
            navigator.mediaDevices.ondevicechange = null; // Cleanup event listener
        };
    }, [appMicAccess]);


    return (
        <div className={classes.container} data-testid="microphone-check-container">
            {errorCode ? (
                <ErrorCard errorCode={errorCode} />
            ) : (
                <>

                    {/* {display errorcard when no micAccess selecte} */}
                    {selectedMic?.deviceId?.trim() === '' && appMicAccess ? (
                        <MicrophoneErrorCard noMicAccess={false} />
                    ) : null}

                    {isMicSelected && !isSpeakCheckFinished && selectedMic?.deviceId.trim() !== '' ? (
                        <Card className={classes.speakCheck}>
                            <SpeakCheckScreen
                                fontSize={fontSize}
                                silenceMeanValue={silenceMeanValue}
                                selectedMic={selectedMic}
                                setIsSpeakCheckFinished={setIsSpeakCheckFinished}
                                downLoadSpeed={downloadSpeed}
                                rules={rules}
                                workerRef={workerRef}
                                ready={ready}
                            />
                        </Card>
                    ) : null}
                    {isSpeakCheckFinished && selectedMic?.deviceId.trim() !== '' ? (
                        <Card className={classes.speakCheck}>
                            <p>
                                <Text text="Thank you. Your microphone is working fine." />
                            </p>
                            <p>
                                <Text text="Click <b>Next</b> to move to the next part." />
                            </p>
                        </Card>
                    ) : (
                        ''
                    )}
                </>
            )}
        </div>
    );
}

/* Display an error message when the user denies microphone access */
export const MicrophoneErrorScreen = (props: MicrophoneErrorScreenProps) => {
    const { isMicAccessEnabled, loading } = props;

    return (
        <>
            <p>
                <Text text="Checking microphone access" variant="heading" fontWeight="semibold" />
            </p>

            {loading && (
                <p>
                    <Text text="Please wait" fontWeight="semibold" />
                </p>
            )}
            {!loading && !isMicAccessEnabled && (
                <>
                    <p>
                        <Text text="Microphone access denied by user. Unable to proceed with test." />
                    </p>
                    <p>
                        <Text text="Please allow access to your microphone and refresh the page." />
                    </p>
                </>
            )}
        </>
    );
};
